import dayjs from 'dayjs';
import { lowerFirst } from 'lodash';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { AccessPermissions, NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridTableContext, useNotificationContext } from 'core/states';
import { CorporateLocationHelper } from 'corporate/helpers';
import { orderPartsPath } from 'orders/routes/routePaths';
import {
  CustomerQuoteUpsertForm,
  SET_AS_DEFAULT_FOR_CONTACT_HIDDEN_FIELDS,
  useAlternateDefaultValues,
} from 'quotes/components/CustomerQuoteUpsert';
import {
  ICreateCustomerQuoteFromQuoteForm as IDuplicateQuoteForm,
  ItemsToCopyField,
  ItemsToCopyFormFields,
  mapAddPartsToQuoteForm,
} from 'quotes/components/QuotePartAddToQuote';
import { DEFAULT_VALID_UNTIL_OFFSET } from 'quotes/constants';
import { QuoteEntityType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { quotePartsPath } from 'quotes/routes/routePaths';
import { ICustomerQuote } from 'quotes/types';

import { duplicateQuoteSchema } from './schema';

const ITEMS_TO_COPY_DEFAULTS = Object.fromEntries(
  Object.values(ItemsToCopyField).map((field) => [field, true])
) as Record<ItemsToCopyField, boolean>;

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: QuoteEntityType;
}

export const DuplicateQuote = ({ isOpen, onClose, entityType = QuoteEntityType.QUOTE }: IProps) => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { selectedRows, deselectAllRows } = useAgGridTableContext();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { handleCliJob } = useCliJobHandler();
  const { addPartsToCustomerQuote } = useQuoteActions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const omitDefaultSalesLocation = hasPermission(
    AccessPermissions.QUOTE_OMIT_DEFAULT_SALES_LOCATION
  );

  const alternateDefaultValues = useAlternateDefaultValues({
    entityType,
    isAlternateVersionEnabled,
  });

  const customDefaultValues = useMemo(() => {
    const validUntilDefaultValue =
      entityType === QuoteEntityType.QUOTE ? dayjs().add(DEFAULT_VALID_UNTIL_OFFSET, 'day') : null;

    const sourceQuote = selectedRows[0];

    return {
      jobName: '',
      location:
        !omitDefaultSalesLocation &&
        user &&
        user.location &&
        CorporateLocationHelper.isSales(user.location, isAlternateVersionEnabled)
          ? {
              id: user.location.id,
              name: user.location.name,
            }
          : null,
      validUntil: validUntilDefaultValue,
      ...alternateDefaultValues,
      pricingDate:
        sourceQuote?.pricingDate && dayjs(sourceQuote.pricingDate).isAfter(dayjs(), 'day')
          ? dayjs(sourceQuote.pricingDate)
          : null,
      // The clone posts a create payload, so this is required — source quotes predating the
      // feature have none, hence the fall back to the user default.
      shippingLocation: CorporateLocationHelper.getDefaultShipping(
        [sourceQuote?.shippingLocation, user?.shippingLocation],
        isAlternateVersionEnabled
      ),
      ...ITEMS_TO_COPY_DEFAULTS,
    };
  }, [
    entityType,
    isAlternateVersionEnabled,
    alternateDefaultValues,
    user,
    omitDefaultSalesLocation,
    selectedRows,
  ]);

  const onSubmit =
    (reset: UseFormReset<IDuplicateQuoteForm>) => async (data: IDuplicateQuoteForm) => {
      const sourceQuote = selectedRows[0];

      handleCliJob(
        addPartsToCustomerQuote,
        mapAddPartsToQuoteForm({
          sourceQuote,
          toQuoteType: sourceQuote.type,
          data,
          isAlternateVersionEnabled,
          entityType,
        }),
        {
          cliJobInProgressMessage: t('messageQuoteDuplicateInProgress', {
            entityType: lowerFirst(entityType),
          }),
          onCliJobFinished: (cliJob: ICliJob) => {
            const { quote } = cliJob.result as Record<string, ICustomerQuote>;

            handleOnClose(reset);
            clearNotification();
            deselectAllRows();

            if (QuoteEntityType.ORDER === entityType) {
              navigate(orderPartsPath(quote.id));

              return;
            }

            navigate(quotePartsPath(quote.id));
          },
          notificationPosition: NotificationPosition.FORM,
        }
      );
    };

  const handleOnClose = (reset: UseFormReset<IDuplicateQuoteForm>) => {
    onClose();

    reset(customDefaultValues);
  };

  return (
    <FormProvider
      schema={duplicateQuoteSchema(entityType, user.companySettings, isAlternateVersionEnabled)}
      defaultValues={customDefaultValues}
      resetDefaultValues
    >
      <CustomerQuoteUpsertForm<IDuplicateQuoteForm>
        title={t('titleDuplicateQuote', { entityType, id: selectedRows[0].id })}
        subtitle={selectedRows[0].jobName}
        isOpen={isOpen}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        onSubmit={onSubmit}
        onClose={handleOnClose}
        user={user}
        defaultValues={customDefaultValues}
        entityType={entityType}
        hiddenFields={SET_AS_DEFAULT_FOR_CONTACT_HIDDEN_FIELDS}
        extraFields={
          <ItemsToCopyFormFields
            quote={selectedRows[0]}
            addToQuoteType={selectedRows[0].type}
            itemsToCopyValues={Object.values(ItemsToCopyField)}
            entityType={entityType}
          />
        }
      />
    </FormProvider>
  );
};
